import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@heroui/react';
import cn from 'classnames';
import { useCheckResetTokenQuery, useResetPasswordMutation } from '@/features/Auth';
import { Svg } from '@/shared/ui/Svg';
import { PageLoader } from '@/shared/ui/Loader';
import { AppButton } from '@/shared/ui/AppButton';
import { Container } from '@/shared/ui/Container';
import { useToast } from '@/shared/lib/hooks';
import { isPasswordResetTokenUsable } from '@/shared/lib/utils/passwordResetToken';
import { routerPath } from '@/shared/config/router';
import LogoIcon from '@/shared/assets/icons/complex/logo/logo-black.complex.svg';
import SlashedEyeIcon from '@/shared/assets/icons/slashed-eye.svg';
import OpenedEyeIcon from '@/shared/assets/icons/opened-eye.svg';
import styles from './ResetPasswordView.module.scss';

interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasSpecial: boolean;
  hasNumber: boolean;
}

const checkPasswordRequirements = (password: string): PasswordRequirements => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  hasNumber: /\d/.test(password),
});

type LinkError = 'invalid' | 'blocked';

const linkErrorFromMessage = (message: string | undefined): LinkError | null => {
  if (message === 'Account is blocked. Please contact administrator.') return 'blocked';
  if (
    message === 'Invalid reset code' ||
    message === 'Token has expired. Please request a new password reset link.'
  ) {
    return 'invalid';
  }
  return null;
};

export const ResetPasswordView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  const token = searchParams.get('token');
  const isTokenWellFormed = isPasswordResetTokenUsable(token);
  const [linkError, setLinkError] = useState<LinkError | null>(
    token && !isTokenWellFormed ? 'invalid' : null,
  );
  const { data: tokenStatus, isLoading: isCheckingToken } = useCheckResetTokenQuery(token ?? '', {
    skip: !isTokenWellFormed,
  });

  const requirements = checkPasswordRequirements(newPassword);
  const allRequirementsMet = Object.values(requirements).every((req) => req === true);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  useEffect(() => {
    if (!token) {
      navigate(routerPath.forgotPassword);
    }
  }, [token, navigate]);

  const handleSubmit = async () => {
    if (!allRequirementsMet || !passwordsMatch || !token) {
      return;
    }

    try {
      await resetPassword({
        code: token,
        password: newPassword,
      }).unwrap();
      setPasswordUpdated(true);
      toast({
        title: 'Success',
        description: 'Your password has been successfully reset',
        color: 'success',
      });
    } catch (error: any) {
      const message = error?.data?.message as string | undefined;
      const nextLinkError = linkErrorFromMessage(message);
      if (nextLinkError) {
        setLinkError(nextLinkError);
        return;
      }
      toast({
        title: 'Error',
        description: message || 'Failed to reset password',
        color: 'danger',
      });
    }
  };

  const checkedLinkError: LinkError | null =
    tokenStatus === 'blocked'
      ? 'blocked'
      : tokenStatus === 'invalid' || tokenStatus === 'expired'
        ? 'invalid'
        : null;
  const visibleLinkError = linkError ?? checkedLinkError;

  if (isCheckingToken) {
    return <PageLoader />;
  }

  if (visibleLinkError === 'blocked') {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Account is blocked</h1>
            <p className={styles.message}>Please contact administrator.</p>
          </div>
        </Container>
      </div>
    );
  }

  if (visibleLinkError === 'invalid') {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Link is invalid or expired</h1>
            <p className={styles.message}>
              This link cannot be used. Request a new one with Forgot password.
            </p>
            <AppButton
              className={styles.button}
              color="grey"
              onPress={() => navigate(routerPath.forgotPassword)}
            >
              Forgot password
            </AppButton>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => navigate(routerPath.login)}
            >
              Back to Sign in
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (passwordUpdated) {
    return (
      <div className={cn(styles.root)}>
        <Container className={styles.container}>
          <div className={styles.wrapper}>
            <Svg className={styles.logo} Icon={LogoIcon} />
            <h1 className={styles.title}>Password successfully updated</h1>
            <p className={styles.message}>You can now sign in with your new password.</p>
            <div className={styles.successIcon}>
              <div className={styles.checkmark}>✓</div>
            </div>
            <AppButton
              className={styles.button}
              color="grey"
              onPress={() => navigate(routerPath.login)}
            >
              Sign in now
            </AppButton>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={cn(styles.root)}>
      <Container className={styles.container}>
        <div className={styles.wrapper}>
          <Svg className={styles.logo} Icon={LogoIcon} />
          <h1 className={styles.title}>Set New Password</h1>
          <div className={styles.form}>
            <div className={styles.inputContainer}>
              <div className={styles.field}>
                <label className={styles.label}>New Password</label>
                <Input
                  type={isNewPasswordVisible ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onValueChange={setNewPassword}
                  variant="bordered"
                  radius="sm"
                  isInvalid={newPassword !== '' && !allRequirementsMet}
                  classNames={{
                    inputWrapper: cn(
                      styles.inputWrapper,
                      newPassword !== '' && !allRequirementsMet
                        ? 'border-red-500 focus-within:border-red-500'
                        : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
                    ),
                  }}
                  endContent={
                    <button
                      type="button"
                      className={styles.eyeButton}
                      onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}
                      aria-label="toggle password visibility"
                    >
                      <Svg
                        Icon={isNewPasswordVisible ? SlashedEyeIcon : OpenedEyeIcon}
                        width={20}
                        height={20}
                        className="text-grey-secondary"
                      />
                    </button>
                  }
                />
                {newPassword !== '' && !allRequirementsMet && (
                  <p className={styles.errorText}>
                    Please add all necessary characters to create safe password:
                  </p>
                )}
              </div>

              {newPassword !== '' && (
                <div className={styles.requirements}>
                  <ul className={styles.requirementsList}>
                    <li
                      className={
                        requirements.minLength ? styles.requirementMet : styles.requirement
                      }
                    >
                      Minimum characters 8
                    </li>
                    <li
                      className={
                        requirements.hasUppercase ? styles.requirementMet : styles.requirement
                      }
                    >
                      One uppercase character
                    </li>
                    <li
                      className={
                        requirements.hasLowercase ? styles.requirementMet : styles.requirement
                      }
                    >
                      One lowercase character
                    </li>
                    <li
                      className={
                        requirements.hasSpecial ? styles.requirementMet : styles.requirement
                      }
                    >
                      One special character
                    </li>
                    <li
                      className={
                        requirements.hasNumber ? styles.requirementMet : styles.requirement
                      }
                    >
                      One number
                    </li>
                  </ul>
                </div>
              )}

              <div className={styles.field}>
                <label className={styles.label}>Confirm New Password</label>
                <Input
                  type={isConfirmPasswordVisible ? 'text' : 'password'}
                  placeholder="Enter your confirm new password"
                  value={confirmPassword}
                  onValueChange={setConfirmPassword}
                  variant="bordered"
                  radius="sm"
                  isInvalid={confirmPassword !== '' && !passwordsMatch}
                  classNames={{
                    inputWrapper: cn(
                      styles.inputWrapper,
                      confirmPassword !== '' && !passwordsMatch
                        ? 'border-red-500 focus-within:border-red-500'
                        : 'border-[#E5E0EB] hover:border-black-DEFAULT focus-within:border-black-DEFAULT',
                    ),
                  }}
                  endContent={
                    <button
                      type="button"
                      className={styles.eyeButton}
                      onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                      aria-label="toggle password visibility"
                    >
                      <Svg
                        Icon={isConfirmPasswordVisible ? SlashedEyeIcon : OpenedEyeIcon}
                        width={20}
                        height={20}
                        className="text-grey-secondary"
                      />
                    </button>
                  }
                />
              </div>
            </div>

            <AppButton
              className={styles.button}
              color="grey"
              onPress={handleSubmit}
              disabled={!allRequirementsMet || !passwordsMatch || isLoading}
            >
              {isLoading ? 'Loading...' : 'Save new password'}
            </AppButton>
            <button
              type="button"
              className={styles.backLink}
              onClick={() => navigate(routerPath.login)}
            >
              Back to Sign in
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};
